import { useEffect, useState } from 'react';
import { deleteField, Timestamp } from 'firebase/firestore';
import Button from '../../../../components/Button';
import type { JourneyState } from '../../services/journeyTypes';

interface JourneyStateEditorProps {
    journeyState: JourneyState;
    onSave: (patch: Partial<JourneyState>) => Promise<void>;
}

type NumericStateKey =
    | 'currentChapterNumber'
    | 'dayNumber'
    | 'energy'
    | 'usableBrainPoints'
    | 'dailyGainedBrainPoints'
    | 'dailyMaxBrainPoints';

const NUMERIC_FIELDS: { key: NumericStateKey; label: string }[] = [
    { key: 'currentChapterNumber', label: 'Current Chapter Number' },
    { key: 'dayNumber', label: 'Day Number' },
    { key: 'energy', label: 'Energy' },
    { key: 'usableBrainPoints', label: 'Usable Brain Points' },
    { key: 'dailyGainedBrainPoints', label: 'Daily Gained Brain Points' },
    { key: 'dailyMaxBrainPoints', label: 'Daily Max Brain Points' },
];

const formatTimestamp = (value: JourneyState['meterFilledAt']): string => {
    if (!value) return 'Not set (user can continue lessons)';
    if (value instanceof Timestamp) return value.toDate().toLocaleString();
    return 'pending write';
};

const pad = (n: number): string => String(n).padStart(2, '0');

// Format a Date to the value expected by <input type="datetime-local"> in local time.
const toDateTimeLocal = (value: JourneyState['meterFilledAt']): string => {
    if (!(value instanceof Timestamp)) return '';
    const d = value.toDate();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const JourneyStateEditor = ({ journeyState, onSave }: JourneyStateEditorProps) => {
    const buildForm = (state: JourneyState): Record<NumericStateKey, string> => {
        const form = {} as Record<NumericStateKey, string>;
        for (const { key } of NUMERIC_FIELDS) {
            form[key] = String(state[key] ?? 0);
        }
        return form;
    };

    const [form, setForm] = useState<Record<NumericStateKey, string>>(() => buildForm(journeyState));
    const [meterInput, setMeterInput] = useState<string>(() => toDateTimeLocal(journeyState.meterFilledAt));
    const [isSaving, setIsSaving] = useState(false);
    const [isMeterSaving, setIsMeterSaving] = useState(false);

    useEffect(() => {
        setForm(buildForm(journeyState));
        setMeterInput(toDateTimeLocal(journeyState.meterFilledAt));
    }, [journeyState]);

    const handleChange = (key: NumericStateKey, value: string) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const saveMeter = async (value: JourneyState['meterFilledAt']) => {
        setIsMeterSaving(true);
        try {
            await onSave({ meterFilledAt: value });
        } finally {
            setIsMeterSaving(false);
        }
    };

    const handleMeterSaveFromInput = async () => {
        if (!meterInput) return;
        const date = new Date(meterInput);
        if (Number.isNaN(date.getTime())) return;
        await saveMeter(Timestamp.fromDate(date));
    };

    const handleMeterSetNow = async () => {
        await saveMeter(Timestamp.now());
    };

    const handleMeterClear = async () => {
        await saveMeter(deleteField());
    };

    const handleSave = async () => {
        const patch: Partial<JourneyState> = {};
        for (const { key } of NUMERIC_FIELDS) {
            const parsed = Number(form[key]);
            if (Number.isNaN(parsed)) continue;
            if (parsed !== journeyState[key]) {
                patch[key] = parsed;
            }
        }
        if (Object.keys(patch).length === 0) return;

        setIsSaving(true);
        try {
            await onSave(patch);
        } finally {
            setIsSaving(false);
        }
    };

    const inputClasses = 'px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';

    return (
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="mb-4 text-xl font-bold text-blue-800">Journey State</h2>

            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                {NUMERIC_FIELDS.map(({ key, label }) => (
                    <div key={key} className="flex flex-col gap-1">
                        <label htmlFor={`state-${key}`} className="text-xs font-medium text-gray-500">
                            {label}
                        </label>
                        <input
                            id={`state-${key}`}
                            type="number"
                            value={form[key]}
                            onChange={(e) => handleChange(key, e.target.value)}
                            className={inputClasses}
                            disabled={isSaving}
                        />
                    </div>
                ))}
            </div>

            <div className="mt-4">
                <Button onClick={handleSave} variant="primary" loading={isSaving} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save State'}
                </Button>
            </div>

            <div className="mt-6 border-t border-gray-100 pt-4">
                <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-gray-500">Meter Filled At</span>
                    <p className="text-sm text-gray-700">
                        Current: <strong>{formatTimestamp(journeyState.meterFilledAt)}</strong>
                    </p>
                    <p className="text-xs text-gray-500">
                        When set on the current calendar day (after 4 AM in the user's timezone), the progress
                        header shows "Back tomorrow" and the user cannot start new lessons. It clears
                        automatically on the next calendar day; clear it here to let the user continue now.
                    </p>
                </div>

                <div className="mt-3 flex flex-wrap items-end gap-2">
                    <div className="flex flex-col gap-1">
                        <label htmlFor="state-meterFilledAt" className="text-xs font-medium text-gray-500">
                            Set to specific time
                        </label>
                        <input
                            id="state-meterFilledAt"
                            type="datetime-local"
                            value={meterInput}
                            onChange={(e) => setMeterInput(e.target.value)}
                            className={inputClasses}
                            disabled={isMeterSaving}
                        />
                    </div>
                    <Button
                        onClick={handleMeterSaveFromInput}
                        variant="primary"
                        loading={isMeterSaving}
                        disabled={isMeterSaving || !meterInput}
                    >
                        Save Time
                    </Button>
                    <Button onClick={handleMeterSetNow} variant="secondary" disabled={isMeterSaving}>
                        Set to Now
                    </Button>
                    <Button onClick={handleMeterClear} variant="danger" disabled={isMeterSaving}>
                        Clear Meter
                    </Button>
                </div>
            </div>
        </div>
    );
};
